import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { AlertTriangle, CheckCircle2, Keyboard, LogIn, LogOut, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { playSound } from "./Notifications";
import { Panel } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const TAG = { prenotato: "tag-teal", effettuato: "bg-green-100 text-green-800", uscito: "bg-slate-200 text-slate-700", "non effettuato": "bg-red-100 text-red-700" };
export const StatoTag = ({ r }) => (
  <span className={`tag capitalize ${TAG[r.stato] || ""}`} data-testid={`park-stato-${r.id}`}>
    {r.stato === "effettuato" ? "Accesso effettuato" : r.stato}
  </span>
);

export const markIngresso = async (r) => {
  try { const x = await api(`/web/parking/requests/${r.id}/ingresso`, { method: "POST" }); toast.success(`ACCESSO EFFETTUATO: ${x.targa}`); return x; }
  catch (e) { toast.error(e.message); return null; }
};

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "");
const splitDir = (raw) => { const m = /^(IN|OU)(?=https?:|[0-9a-f]{8})/i.exec(raw); return m ? [raw.slice(2), m[1].toUpperCase() === "OU" ? "out" : "in"] : [raw, "in"]; };
const OUT_MSG = { prenotato: "L'auto non risulta entrata: uscita non registrata.", "non effettuato": "Accesso mai effettuato: uscita non registrata." };

function Camera({ onCode }) {
  const ref = useRef(null);
  useEffect(() => {
    const q = new Html5Qrcode("park-qr-reader");
    ref.current = q;
    let done = false;
    q.start({ facingMode: "environment" }, { fps: 10, qrbox: 230 }, (txt) => { if (!done) { done = true; onCode(txt); } })
      .catch(() => toast.error("Impossibile aprire la fotocamera: consenti l'accesso dal browser"));
    return () => { if (q.isScanning) { q.stop().catch(() => {}); } };
  }, [onCode]);
  return <div id="park-qr-reader" className="w-full max-w-sm overflow-hidden rounded-xl bg-black" data-testid="park-qr-reader" />;
}

function ReaderInput({ onCode }) {
  const [v, setV] = useState("");
  const box = useRef(null);
  const submit = (val = v) => { const c = val.trim(); setV(""); if (c) onCode(...splitDir(c)); setTimeout(() => box.current?.focus(), 50); };
  useEffect(() => {
    if (!/[0-9a-f]{8}.[0-9a-f]{4}.[0-9a-f]{4}.[0-9a-f]{4}.[0-9a-f]{12}$/i.test(v.trim())) { return undefined; }
    const t = setTimeout(() => submit(v), 250);
    return () => clearTimeout(t);
  }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const key = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) { return; }
      box.current?.focus();
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, []);
  return (
    <div className="flex gap-2" data-testid="park-reader">
      <div className="relative flex-1"><Keyboard className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input ref={box} autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder="Lettore codici: leggi il QR Code, la verifica parte da sola" className="pl-9" data-testid="park-reader-input" /></div>
      <Button variant="outline" className="rounded-full" onClick={() => submit()} data-testid="park-reader-submit">Verifica</Button>
    </div>
  );
}

export function SecondScan({ r }) {
  const ora = r.ingresso_at ? new Date(r.ingresso_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "";
  return (
    <div className="mt-2 rounded-xl bg-red-600 text-white p-3 flex gap-2 items-start" role="alert" data-testid="park-second-scan">
      <AlertTriangle className="w-6 h-6 shrink-0" />
      <p className="font-semibold">QR Code già utilizzato oggi{ora ? ` alle ${ora}` : ""}{r.ingresso_by ? ` (convalidato da ${r.ingresso_by})` : ""}. Una volta entrati non è possibile uscire e rientrare: non consentire un nuovo accesso.</p>
    </div>
  );
}

export function ParkScanner({ onChange }) {
  const [on, setOn] = useState(false);
  const [r, setR] = useState(null);
  const [again, setAgain] = useState(false);
  const [dir, setDir] = useState("in");
  const [outMsg, setOutMsg] = useState("");
  const [barrier, setBarrier] = useState(null);
  const exit = async (x) => {
    if (x.stato !== "effettuato") { setR(x); setOutMsg(x.stato === "uscito" ? `Uscita già registrata${x.uscita_at ? ` alle ${hhmm(x.uscita_at)}` : ""}.` : OUT_MSG[x.stato] || ""); playSound("alert"); return; }
    try { const y = await api(`/web/parking/requests/${x.id}/uscita`, { method: "POST" }); setR(y); setBarrier({ out: true, targa: y.targa || "" }); playSound("barrier"); onChange?.(); }
    catch (e) { setR(x); setOutMsg(e.message); playSound("alert"); }
  };
  const onCode = async (code, d = "in") => {
    setOn(false); setDir(d); setOutMsg(""); setAgain(false); setBarrier(null);
    try {
      const x = await api("/web/parking/scan", { method: "POST", body: { code } });
      if (d === "out") return exit(x);
      setR(x); setAgain(x.stato === "effettuato" || x.stato === "uscito"); if (x.stato === "effettuato" || x.stato === "uscito") playSound("alert");
    } catch (e) { setR(null); toast.error(e.message); }
  };
  const confirm = async () => { const x = await markIngresso(r); if (x) { setR(x); setAgain(false); setBarrier({ out: false, targa: x.targa || "" }); playSound("barrier"); onChange?.(); } };
  return (
    <Panel className="space-y-4" data-testid="park-scanner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-[var(--navy)]">Portineria · controllo ingressi e uscite</h2>
        {on ? <Button variant="outline" className="rounded-full" onClick={() => setOn(false)} data-testid="park-scan-stop"><X className="w-4 h-4 mr-1" />Chiudi fotocamera</Button>
          : <Button className="rounded-full bg-[var(--navy)]" onClick={() => { setR(null); setOn(true); }} data-testid="park-scan-start"><ScanLine className="w-4 h-4 mr-1" />Leggi QR Code</Button>}
      </div>
      {on && <Camera onCode={onCode} />}
      <ReaderInput onCode={onCode} />
      {barrier && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" role="alertdialog" data-testid="park-barrier-popup">
          <div className={`w-full max-w-lg rounded-3xl p-8 text-center shadow-2xl ${barrier.out ? "bg-yellow-400 text-black" : "bg-green-600 text-white"}`} data-testid="park-barrier-box">
            {barrier.out ? <LogOut className="w-14 h-14 mx-auto mb-4" /> : <LogIn className="w-14 h-14 mx-auto mb-4" />}
            <p className="font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="park-barrier-text">{barrier.out ? "APRIRE LA SBARRA DI USCITA, GRAZIE" : "APRIRE LA SBARRA DI INGRESSO, GRAZIE"}</p>
            {barrier.targa && <p className="mt-3 font-mono text-xl font-bold">{barrier.targa}</p>}
            <Button onClick={() => setBarrier(null)} className={`mt-6 h-12 px-10 rounded-full font-bold ${barrier.out ? "bg-black text-yellow-400 hover:bg-black/85" : "bg-white text-green-700 hover:bg-white/90"}`} data-testid="park-barrier-close">OK</Button>
          </div>
        </div>
      )}
      {r && (
        <div className="rounded-xl border p-4 space-y-1 text-sm" data-testid="park-scan-result">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`tag ${dir === "out" ? "bg-slate-800 text-white" : "bg-[var(--navy)] text-white"}`} data-testid="park-scan-dir">{dir === "out" ? <><LogOut className="w-3 h-3 inline mr-1" />Lettore OUT</> : <><LogIn className="w-3 h-3 inline mr-1" />Lettore IN</>}</span>
            <span className="font-mono font-bold text-lg text-[var(--navy)]">{r.targa || "Targa da inserire"}</span><StatoTag r={r} />
          </div>
          <p><b>{r.nominativo}</b> · {r.nome_park} ({r.tipo})</p>
          <p>{r.data_label} · {r.turno_label}</p>
          {(r.ingresso_at || r.uscita_at) && <p className="text-slate-600" data-testid="park-scan-times">{r.ingresso_at && `Ingresso ${hhmm(r.ingresso_at)}`}{r.uscita_at && ` · Uscita ${hhmm(r.uscita_at)}`}</p>}
          {dir === "out" && r.uscita_at && !outMsg && <p className="mt-2 rounded-xl bg-green-600 text-white p-3 font-semibold" data-testid="park-exit-ok">USCITA REGISTRATA alle {hhmm(r.uscita_at)}: stato Uscito, il posto è di nuovo libero.</p>}
          {dir === "out" && r.contatori && !outMsg && <p className="text-slate-700" data-testid="park-exit-counters">Auto nel turno: <b>{r.contatori.turno_used}/{r.contatori.total_posti}</b> · {r.contatori.host_name || "Ospitante"}: <b>{r.contatori.host_used}{r.contatori.host_limit != null ? `/${r.contatori.host_limit}` : ""}</b></p>}
          {outMsg && <div className="mt-2 rounded-xl bg-red-600 text-white p-3 flex gap-2 items-start" role="alert" data-testid="park-exit-warning"><AlertTriangle className="w-6 h-6 shrink-0" /><p className="font-semibold">{outMsg}</p></div>}
          {dir === "in" && again && <SecondScan r={r} />}
          {dir === "in" && r.stato === "prenotato" && !r.targa && <p className="mt-2 rounded-lg bg-amber-50 text-amber-800 p-2 font-medium" data-testid="park-scan-no-plate">Targa mancante: l'ospite deve inserirla dal link del QR Code prima di poter convalidare l'accesso.</p>}
          {dir === "in" && r.stato === "prenotato" && r.targa && <Button onClick={confirm} className="mt-2 rounded-full bg-green-600 hover:bg-green-700" data-testid="park-scan-confirm"><CheckCircle2 className="w-4 h-4 mr-1" />CONVALIDA ACCESSO</Button>}
        </div>
      )}
    </Panel>
  );
}
